import React from "react";
import ReactDOM from "react-dom";
import { usePromiseTracker } from "react-promise-tracker";
import App from "./App";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { Constants } from "./utils/constants";
import { watchLocationPermission } from "./utils/locationUtils";
import { Buffer } from "buffer";
import logger from "./utils/logger";
import "./styles/tailwind.css"; // Import Tailwind CSS
import "react-datepicker/dist/react-datepicker.css"; // Global DatePicker styles
window.Buffer = Buffer;

// Suppress ResizeObserver errors - these are harmless and happen when React renders too fast
const resizeObserverErr = window.console.error;
window.console.error = (...args) => {
  const firstArg = args[0];
  if (
    typeof firstArg === "string" &&
    (firstArg.includes("ResizeObserver loop") ||
      firstArg.includes("ResizeObserver loop completed with undelivered notifications"))
  ) {
    return;
  }
  resizeObserverErr(...args);
};

// Also catch it at the window level
window.addEventListener("error", e => {
  if (e.message && e.message.includes("ResizeObserver loop")) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
});

// Catch unhandled promise rejections that might contain this error
window.addEventListener("unhandledrejection", e => {
  if (e.reason && e.reason.message && e.reason.message.includes("ResizeObserver loop")) {
    e.preventDefault();
    return false;
  }
});

logger.log("[SPACIA_INIT] 1. Bundle execution started", {
  url: typeof window !== "undefined" ? window.location.href : "unknown",
  hostname: typeof window !== "undefined" ? window.location.hostname : "unknown",
});

// Add detailed logging
const debugLog = (section, ...args) => {
  logger.log(`[SPACIA_INIT] [${section}]`, ...args);
};

// Watch the browser's geolocation permission state for the lifetime of the app.
// If the visitor revokes location access (browser settings, OS settings, or
// DevTools) we drop any stored fix immediately, so we never keep sending their
// coordinates as request headers after they have switched location off.
watchLocationPermission(state => {
  debugLog("Location Permission", `geolocation permission = ${state}`);
});

// Check browser compatibility first
const checkBrowserCompatibility = () => {
  debugLog("Browser Check", "Starting compatibility check");

  const isFirefox = navigator.userAgent.includes("Firefox");
  const hasServiceWorker = "serviceWorker" in navigator;

  debugLog("Feature Detection", {
    browser: isFirefox ? "Firefox" : "Other",
    serviceWorker: hasServiceWorker,
    messaging: "Messaging" in window,
  });

  return { isFirefox, isPrivateMode: !hasServiceWorker, hasServiceWorker };
};

const LoadingIndicator = () => {
  const { promiseInProgress } = usePromiseTracker();
  return promiseInProgress && <h1>Hey some async call in progress!</h1>;
};

// Initialize Firebase only if browser supports it
let app, messaging;
const browserSupport = checkBrowserCompatibility();

if (browserSupport.hasServiceWorker && !browserSupport.isPrivateMode) {
  try {
    debugLog("Firebase", "Initializing Firebase");
    app = initializeApp(Constants.firebaseConfig);
    const analytics = getAnalytics(app);

    // Enable debug mode for development
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      window["ga-disable-" + Constants.firebaseConfig.measurementId] = false;
      debugLog("Firebase", "Debug mode enabled for localhost");
    }

    messaging = getMessaging(app);
    debugLog("Firebase", "Firebase initialized successfully");
  } catch (error) {
    debugLog("Firebase Error", error);
  }
} else {
  debugLog("Firebase", "Skipping messaging init - browserSupport check returned false", browserSupport);
}

// Export browser support info for use in App component
export const browserSupportInfo = browserSupport;

// Function to display FCM token on the page
// const displayFCMToken = (token) => {
// 	const tokenDisplay = document.createElement('div');
// 	tokenDisplay.style.position = 'fixed';
// 	tokenDisplay.style.top = '10px';
// 	tokenDisplay.style.left = '10px';
// 	tokenDisplay.style.padding = '10px';
// 	tokenDisplay.style.background = 'rgba(0,0,0,0.8)';
// 	tokenDisplay.style.color = 'white';
// 	tokenDisplay.style.zIndex = '9999';
// 	tokenDisplay.style.maxWidth = '80%';
// 	tokenDisplay.style.wordBreak = 'break-all';
// 	tokenDisplay.innerHTML = `<strong>FCM Token:</strong><br>${token}<br><button onclick="navigator.clipboard.writeText('${token}')">Copy Token</button>`;
// 	document.body.appendChild(tokenDisplay);
// };

// Request permission and get token
const requestPermissionAndGetToken = async () => {
  if (!messaging) return;
  try {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      const token = await getToken(messaging, {
        vapidKey: "BIm2bfND4GK7_4_BorI_nwdz_ivc00n7_nom7Tn3YSid7THcokJAB81-n8xoqOBgjMz7WZ1J7Guk6wR3saZiWgs",
        serviceWorkerRegistration: registration,
      });
      logger.log("[SPACIA_INIT] FCM Token generated:", token);
    } else {
      logger.log("[SPACIA_INIT] Notification permission denied");
    }
  } catch (error) {
    logger.error("[SPACIA_INIT] Error in requestPermissionAndGetToken:", error);
  }
};

if (messaging) {
  requestPermissionAndGetToken();

  const onMessageListener = () =>
    new Promise(resolve => {
      onMessage(messaging, payload => {
        logger.log("[SPACIA_INIT] Received foreground message ", payload);
        resolve(payload);
      });
    });

  onMessageListener()
    .then(payload => {
      logger.log("[SPACIA_INIT] Foreground message payload:", payload);
      if (payload?.notification) {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: "/assets/img/logo/logo.png",
        });
      }
    })
    .catch(err => {
      logger.error("[SPACIA_INIT] Error in onMessageListener:", err);
    });
}

// ===========================================================
// DEEP LINK INTERCEPT - Must run BEFORE ReactDOM.render
// This fires synchronously before any React/Router code runs,
// ensuring query parameters are never stripped by the router.
// ===========================================================
(function interceptDeepLinks() {
  const search = window.location.search;
  logger.log("[SPACIA_INIT] Checking deep link intercept, search:", search);
  if (!search || !search.includes("action=")) return;

  const params = new URLSearchParams(search);
  const action = (params.get("action") || "").toLowerCase();
  const code = params.get("code") || params.get("token");

  if (!action || !code) return;

  if (["reset_password", "forgot_password", "resetpassword", "savepassword"].includes(action)) {
    window.location.replace(window.location.origin + "/savepassword?code=" + encodeURIComponent(code));
    return;
  }
  if (["confirm_account", "verify", "confirmaccount", "verify_account"].includes(action)) {
    window.location.replace(window.location.origin + "/otp-confirmation?code=" + encodeURIComponent(code));
    return;
  }
  if (["invite_confirmation", "confirm_invite"].includes(action)) {
    window.location.replace(window.location.origin + "/authentication/confirm-invite?code=" + encodeURIComponent(code));
    return;
  }
})();

logger.log("[SPACIA_INIT] Rendering React application root to #root element");

ReactDOM.render(
  <>
    <App browserSupport={browserSupport} />
    <LoadingIndicator />
  </>,
  document.getElementById("root")
);
